import * as Yup from 'yup';
import { useApiGet, useApiPost } from '../../../../services/tanstackApiService';
export { getDefaultDialCode } from '../../../../utils/getDefaultDialCode';

// ─── Registration type discriminator ─────────────────────────────────────────

/** Determines how a walk-in patient was saved: as a "Pharmacy" walk-in or as a fully "Registered" patient with MRN */
export type RegistrationType = 'Pharmacy' | 'Registered';

// ─── Form values, initial values & validation schema ─────────────────────────

export interface NewPatientCreatePayload {
  first_name: string;
  last_name: string;
  mobile_number_country_code: string; // e.g. "+91"
  mobile_number: string;
  gender: number | string | null; // gender id in form, gender name (MALE/FEMALE) in API
  date_of_birth: string; // ISO date string (YYYY-MM-DD) — required
  email: string; // required
}

/** Full payload sent to the register-walkin-patient API */
export interface RegisterPatientAPIPayload extends NewPatientCreatePayload {
  clinic_id: number | string;
  registration_type: RegistrationType;
  is_new?: boolean;
}

export const initialNewPatientValues: NewPatientCreatePayload = {
  first_name: '',
  last_name: '',
  mobile_number_country_code: '',
  mobile_number: '',
  gender: null,
  date_of_birth: '',
  email: '',
};

export const newPatientValidationSchema = Yup.object().shape({
  first_name: Yup.string().trim().required(' '),
  last_name: Yup.string().trim().required(' '),
  mobile_number_country_code: Yup.string()
    .trim()
    .required(' '),
  mobile_number: Yup.string()
    .trim()
    .required(' ')
    .matches(/^[0-9]{7,15}$/, 'Enter a valid mobile number'),
  gender: Yup.string().required(' '),
  date_of_birth: Yup.string().required(' '),
  email: Yup.string().email('Enter a valid email').required(' '),
});

// ---------------------------------------------------------------------
// Selected existing patient shape (read-only card)
// ---------------------------------------------------------------------
export interface ExistingPatientDetail {
  id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  gender_label?: string;
  medical_record_no: string;
  mobile_number: string;
  mobile_number_country_code?: string;
  email?: string;
  photo?: string;
}

// ---------------------------------------------------------------------
// Gender master API types
// ---------------------------------------------------------------------
export interface GenderTranslationMap {
  en: string;
  es: string;
  hi: string;
  pt: string;
}

export interface GenderMasterItem {
  id: number;
  name_label: string;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  deleted_reason: string | null;
  name: string;
  is_active: boolean;
  translations: GenderTranslationMap;
  tenant: number;
}

export interface GenderMasterResponseMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface GenderMasterResponse {
  meta: GenderMasterResponseMeta;
  objects: GenderMasterItem[];
}

// ---------------------------------------------------------------------
// Country dial-code types
// ---------------------------------------------------------------------
export interface CountryDialCodeItem {
  name: string;
  dial_code: string;
}

export interface CountryDialCodeResponse {
  error: boolean;
  msg: string;
  data: CountryDialCodeItem[];
}

// ---------------------------------------------------------------------
// Referral doctor types
// ---------------------------------------------------------------------
export interface ReferralDoctorDepartment {
  id: number;
  name: string;
  clinic__id: number;
  clinic__name: string;
  name_label: string;
  tenant__name: string;
  is_clinical: boolean;
}

export interface ReferralDoctorItem {
  id: number;
  email: string;
  first_name: string;
  gender__name: string | null;
  last_name: string;
  username: string;
  role: string;
  departments: ReferralDoctorDepartment[];
  appointment_type: string | null;
  is_active: boolean;
  user_type: string | null;
  language: number | null;
  language_code: string | null;
  language_name: string | null;
  is_locked: boolean;
  gender__name_label: string | null;
  role_label: string | null;
}

export interface ReferralDoctorResponseMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface ReferralDoctorResponse {
  meta: ReferralDoctorResponseMeta;
  objects: ReferralDoctorItem[];
}

// ---------------------------------------------------------------------
// Existing patients search response (from pharmacy-invoice/patients/)
// ---------------------------------------------------------------------
export interface ExistingPatientObject {
  id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  gender: number | null;
  mobile_number: string;
  mobile_number_country_code: string;
  patient_source: string | null;
  medical_record_no: string;
  registration_type: string | null;
  created_on: string;
  clinic_id: number;
  patient_name: string;
  age: number | null;
  gender_name: string | null;
  full_mobile_number: string;
  clinic_name: string;
  is_new: boolean;
  email?: string;
  photo?: string;
}

export interface ExistingPatientsResponse {
  meta: {
    limit: number;
    next: string | null;
    offset: number;
    previous: string | null;
    total_count: number;
  };
  objects: ExistingPatientObject[];
}

// ---------------------------------------------------------------------
// API URL helpers
// ---------------------------------------------------------------------
export function getGendersURL(): string {
  return 'genders/';
}
export function getCountryCodesURL(): string {
  return 'countries/codes/';
}
export function getReferralDrsURL(): string {
  return 'users/?role=DOCTOR';
}
export function registerPatientURL(): string {
  return 'pharmacy-invoice/register-walkin-patient/';
}
export function getExistingPatient(
  clinicId: number | string,
  search: string
): string {
  const params = new URLSearchParams({
    limit: '20',
    offset: '0',
    clinic: String(clinicId),
  });
  if (search) params.set('search', search);
  return `pharmacy-invoice/patients/?${params.toString()}`;
}

// ---------------------------------------------------------------------
// Mutation hook
// ---------------------------------------------------------------------
export function useRegisterPatient() {
  return useApiPost<unknown, RegisterPatientAPIPayload>();
}

// ---------------------------------------------------------------------
// Combined hook to load all master data
// ---------------------------------------------------------------------
export function useNewPatientMasters() {
  const gendersQuery = useApiGet<GenderMasterResponse>(
    ['new-patient', 'genders'],
    { path: getGendersURL(), query: { limit: 100, offset: 0 } },
    { staleTime: 5 * 60 * 1000 }
  );

  const countryCodesQuery = useApiGet<CountryDialCodeResponse>(
    ['new-patient', 'country-codes'],
    { path: getCountryCodesURL() },
    { staleTime: 60 * 60 * 1000 }
  );

  const referralDoctorsQuery = useApiGet<ReferralDoctorResponse>(
    ['new-patient', 'referral-doctors'],
    { path: getReferralDrsURL() },
    { staleTime: 5 * 60 * 1000 }
  );

  const isLoading =
    gendersQuery.isLoading ||
    countryCodesQuery.isLoading ||
    referralDoctorsQuery.isLoading;

  const isError =
    gendersQuery.isError ||
    countryCodesQuery.isError ||
    referralDoctorsQuery.isError;

  return {
    gendersQuery,
    countryCodesQuery,
    referralDoctorsQuery,
    genders: gendersQuery.data?.objects ?? [],
    countryCodes: countryCodesQuery.data?.data ?? [],
    referralDoctors: referralDoctorsQuery.data?.objects ?? [],
    isLoading,
    isError,
  };
}
